import { IVec3 } from "@base/math/Vec3";
import { mapArrayToRecord, mapRecordToArray } from "@base/util/TypeUtil";

import type { DoubleMountType, MaterialGroup, RemoteType, SunSailType } from "./SunSailDefinitions";
import type { IClothData } from "@biz/logic/ClothData";
import type { SunSailGroup } from "@biz/items/sunSail/logic/SunSailTriangle";
export const backenklemmeLoseArticleId = "00E13000057";

export const backenklemmenSets: Record<string, { klemme: string; holder: string }> = {
  "00E13001905": { klemme: backenklemmeLoseArticleId, holder: "00E13000376" }, // Backenklemme Set Alu
  "00E13001906": { klemme: backenklemmeLoseArticleId, holder: "00E13000375" }, // Backenklemme Set Edelstahl
  "00E13002092": { klemme: backenklemmeLoseArticleId, holder: "00E13000210" }, // Backenklemme Set Droppole
}

export const backenklemmen: string[] = [
  backenklemmeLoseArticleId, // Backenklemme lose
  ...Object.keys(backenklemmenSets)
]

export const backenklemmenWallPlate: string[] = [
  "00E13000088", //Befestigungsplatte Wand für Backenklemme (vetikal)
  "00E13001146", // Befestigungsplatte Wand horizontal
]

export const backenklemmenPoleHolder: string[] = [
  "00E13000375", // Backenklemmenhalter Edelstahl
  "00E13000376", // Backenklemmenhalter Alu
]

export const backenklemmenHalterArticles: string[] = [
  ...backenklemmenWallPlate,
  ...backenklemmenPoleHolder,
  ...Object.keys(backenklemmenSets)
]

export const sicherheitsKlemmenMap: Record<MaterialGroup, string> = {
  "wall": "00E13000030",
  "special": "00E13000030",
  "alu": "00E13000030",
  "steel": "00E13000030",
  "droppole": "00E13000030",
  "iron48": "00E13000030",
  "iron89": "00E13000030",
  "iron114": "00E13000030",
  "telescope": "00E13001230",
  "profile": "00E13001230"
}
export const sicherheitsKlemmen: string[] = mapRecordToArray(sicherheitsKlemmenMap, (from) => from).filter((value, index, self) => index === self.indexOf(value))

export const seilKlemmeMap: Record<MaterialGroup, string> = {
  "wall": "00E13001220",
  "special": "00E13001220",
  "alu": "00E13001220",
  "steel": "00E13001220",
  "droppole": "00E13001220",
  "iron48": "00E13001220",
  "iron89": "00E13001220",
  "iron114": "00E13001220",
  "telescope": "00E13001231",
  "profile": "00E13001231"
}
export const seilKlemmen: string[] = mapRecordToArray(seilKlemmeMap, (from) => from).filter((value, index, self) => index === self.indexOf(value))

export const bedienSetOne: Record<MaterialGroup, string> = {
  "wall": "00E13002077",
  "special": "00E13002077",
  "steel": "00E13002075",
  "alu": "00E13002076",
  "droppole": "00E13002074",
  "iron48": "",
  "iron89": "",
  "iron114": "",
  "telescope": "",
  "profile": ""
}

export interface ICoilUpSizeInfo {
  crankRadius?: number;
  below: number;
  above: number;
  drillOffsets: { offset: number; drill: string }[];
  shortName: string;
  coilUpRopeDiameter?: number;
}

export const crankRadiusReserve = 0.1; // https://plaspack.atlassian.net/browse/APP22-1003

const snapWinch: ICoilUpSizeInfo = {
  shortName: "Snap-Basic Winsch",
  crankRadius: 0.25 + crankRadiusReserve,
  below: 0.05,
  above: 0.215,
  coilUpRopeDiameter: 8,
  drillOffsets: [{ offset: -0.03, drill: "M6" }/*, { offset: 0.19, drill: "M6" }*/] // https://plaspack.atlassian.net/browse/APP22-982
}

const snapDesign: ICoilUpSizeInfo = {
  ...snapWinch,
  shortName: "Snap-Design Winsch",
}

const snapPremium: ICoilUpSizeInfo = {
  ...snapWinch,
  shortName: "Snap-Premium Winsch",
}

const droppoleHeightChange: ICoilUpSizeInfo = {
  shortName: "Droppole Höhenverstellung inkl. Kurbel",
  crankRadius: 0.25 + crankRadiusReserve,
  below: 0.12,
  above: 0.12,
  coilUpRopeDiameter: 0,
  drillOffsets: [{ offset: -0.03, drill: "M6" } /*, { offset: 0.19, drill: "M6" }*/] // https://plaspack.atlassian.net/browse/APP22-982
}

const heightChangeMotor: ICoilUpSizeInfo = {
  shortName: "Höhenverstellung mit Motor",
  below: 0.32,
  above: 0.32,
  coilUpRopeDiameter: 0,
  drillOffsets: [{ offset: -0.03, drill: "M6" } /*, { offset: 0.19, drill: "M6" }*/] // https://plaspack.atlassian.net/browse/APP22-982
}

const backenklemmeCoilUpSizeInfo: ICoilUpSizeInfo = {
  shortName: "Backenklemme",
  below: 0.025,
  above: 0.042,
  coilUpRopeDiameter: 6.5,
  drillOffsets: [{ offset: 0.0, drill: "M5" }]
}

const seilKlemmenCoilUpSizeInfo: ICoilUpSizeInfo = {
  shortName: "Seilklemme",
  below: 0.025,
  above: 0.042,
  coilUpRopeDiameter: 6.5,
  drillOffsets: [{ offset: 0.0, drill: "M4" }] // drill wird noch abeklärt?
}

const sicherheitsKlemmenCoilUpSizeInfo: ICoilUpSizeInfo = {
  shortName: "Sicherheitsklemme",
  below: 0.025,
  above: 0.042,
  coilUpRopeDiameter: 6.5,
  drillOffsets: [{ offset: 0.0, drill: "M4" }]
}

const fallstoppKlemmeCoilUpSizeInfo: ICoilUpSizeInfo = {
  shortName: "Fallstoppklemme",
  crankRadius: 0.25 + crankRadiusReserve,
  below: 0.05,
  above: 0.215,
  coilUpRopeDiameter: 8,
  drillOffsets: [{ offset: -0.03, drill: "M6" }/*, { offset: 0.19, drill: "M6" }*/] // https://plaspack.atlassian.net/browse/APP22-982
}

export const winchInfos: Record<string, ICoilUpSizeInfo> = {
  "": {
    shortName: "???",
    crankRadius: 0.25 + crankRadiusReserve,
    below: 0.06,
    above: 0.06,
    coilUpRopeDiameter: 6,
    drillOffsets: [{ offset: 0.05, drill: "M6" }]
  },
  "one_alu": {
    shortName: "One Bedienset",
    below: 0.06,
    above: 0.06,
    drillOffsets: []
  },
  "one_steel": {
    shortName: "One Bedienset",
    below: 0.06,
    above: 0.06,
    drillOffsets: []
  },
  "one_droppole": {
    shortName: "One Bedienset",
    below: 0.06,
    above: 0.06,
    drillOffsets: []
  },
  "00E13000059": {
    shortName: "Seilstopp-Winsch",
    crankRadius: 0.25 + crankRadiusReserve,
    below: 0.06,
    above: 0.06,
    coilUpRopeDiameter: 10,
    drillOffsets: [{ offset: 0.05, drill: "M8" }] // https://plaspack.atlassian.net/browse/APP22-982
  },
  "00E13001320": snapWinch,
  "00E13000591": snapDesign,
  "00E13001322": snapDesign, // Aufpreis Snap Design
  "00E13001367": snapPremium,
  "00E13001323": snapPremium, // Aufpreis Snap Premium
  ...mapArrayToRecord(sicherheitsKlemmen, (from) => from, (_from) => sicherheitsKlemmenCoilUpSizeInfo),
  ...mapArrayToRecord(backenklemmen, (from) => from, (_from) => backenklemmeCoilUpSizeInfo),
  ...mapArrayToRecord(seilKlemmen, (from) => from, (_from) => seilKlemmenCoilUpSizeInfo),
  "00E13000743": fallstoppKlemmeCoilUpSizeInfo,
  "00E13001631": {
    ...fallstoppKlemmeCoilUpSizeInfo,
    shortName: "Fallstoppklemme und Bügelschloss",
  },
  "00E13000208": droppoleHeightChange,
  "00E13000988": {
    ...droppoleHeightChange,
    shortName: "Custom-Design Droppole Höhenverstellung schwarz"
  },
  "00E13001925": { ...heightChangeMotor, shortName: "Höhenverstellung mit Funk" },
  "00E13001926": { ...heightChangeMotor, shortName: "Höhenverstellung mit Funk" },
  "00E13001927": { ...heightChangeMotor, shortName: "Höhenverstellung mit Funk" },
  "00E13000974": { ...heightChangeMotor, shortName: "Höhenverstellung mit Funk" },
  "00E13000975": { ...heightChangeMotor, shortName: "Höhenverstellung mit Funk" },
  "00E13000976": { ...heightChangeMotor, shortName: "Höhenverstellung mit Funk" },
  "00E13001056": { ...heightChangeMotor, shortName: "Höhenverstellung ohne Funk" },
  "00E13001057": { ...heightChangeMotor, shortName: "Höhenverstellung ohne Funk" },
  "00E13001058": { ...heightChangeMotor, shortName: "Höhenverstellung ohne Funk" },

}
export const fallstoppMap: Record<MaterialGroup, string> = {
  "wall": "00E13000743",
  "special": "00E13000743",
  "alu": "00E13000743",
  "steel": "00E13000743",
  "droppole": "00E13000743",
  "iron48": "00E13000743",
  "iron89": "00E13000743",
  "iron114": "00E13000743",
  "telescope": "00E13002041", // 00E13001332 ? ohne Grundplatte
  "profile": "00E13002041"
}
const fallStoppArticles: string[] = mapRecordToArray(fallstoppMap, (from) => from).filter((value, index, self) => index === self.indexOf(value))
export const fallstoppLockMap: Record<MaterialGroup, string> = {
  "wall": "00E13001631",
  "special": "00E13001631",
  "alu": "00E13001631",
  "steel": "00E13001631",
  "droppole": "00E13001631",
  "iron48": "00E13001631",
  "iron89": "00E13001631",
  "iron114": "00E13001631",
  "telescope": "",
  "profile": ""
}
const fallStoppLockArticles: string[] = mapRecordToArray(fallstoppLockMap, (from) => from).filter((value, index, self) => !!value && index === self.indexOf(value))
export const allFallStoppArticles = [...fallStoppArticles, ...fallStoppLockArticles]

export function calcFallstoppArticles(materialGroup: MaterialGroup | undefined): { default: string; lock: string; } {
  return {
    default: (fallstoppMap as any)[materialGroup || ""] || "",
    lock: (fallstoppLockMap as any)[materialGroup || ""] || "",
  }
}



export const seilStoppWinchArticleId = "00E13000059";
export const snapWinches: string[] = ["00E13001320", "00E13000591", "00E13001367"]; // Basic, Design, Premium
export const allSnapWincheArticleNumbers: string[] = [...snapWinches, "00E13001322", "00E13001323"]; // Aufpreis
export const allWinchArticleNumbers: string[] = [...allSnapWincheArticleNumbers, seilStoppWinchArticleId]
export const allWinchAndFallstopArticleNumbers: string[] = [...allWinchArticleNumbers, ...allFallStoppArticles]

export const sailMountClamps: string[] = [
  ...sicherheitsKlemmen,
  ...seilKlemmen,
  ...backenklemmen
]
export const allWinchLikeArticles: string[] = [...allWinchArticleNumbers, ...allFallStoppArticles, ...sailMountClamps]

export type AxleMountType = "wallShackle" | "keder" | "bolt" | "clamp" | "threaded" | "adapter" | "adapter_buegel" | "direct" | "ringAdapter" | "special";
export type NonAxleMountType = "keder" | "bolt" | "clamp" | "pylon" | "wall" | "special" | "profile" | "telescope" | "heightChange";
export type PointMountType = AxleMountType | NonAxleMountType;
export type ExtendedPointMountType = "rollOnly" | "shackleOnly" | "threadTensioner" | "shacklePlusRoll" | "direct" | "heightChange";

export type MountArticle =
  "00E13000026" | // shackle
  "00E13001151" | // roll C/CS
  "00E13000042" | // roll small
  "00E13001965" | // Schwerlastrolle small
  "00E13001482";  // roll 10mm

export interface IExtendedMountInfo {
  type: ExtendedPointMountType;
  articles?: MountArticle[]
}
export interface IPointMountInfo {
  doubleMount?: DoubleMountType;
  mountType: PointMountType;
  extendedMount: IExtendedMountInfo[];
}


export interface RemoteChannelCount {
  motorChannels: number;
  totalChannels: number;
  sunWindSensorCount: number;
  motorRemoteType: RemoteType | undefined;
}

export interface IAxlePos<T> {
  first: T;
  second: T;
}

export type SunSailTopologyOld = {
  netGroups: SunSailGroup[];
  outerGroups: SunSailGroup[];
};

export interface ICalculatedPoleLength {
  totalLength: number;
  bottomAdd: number;
  topAdd: number;
  shoppingLength: number;
  shoppingLengthCharged: number;
}

export interface IKnotPoints {
  outer: IVec3;
  inner: IVec3;
  mid: IVec3;
  mount: IVec3;
}
export interface IClothColorSelectItem {
  value: string;
  cloth: IClothData;
  colors: string[];
  label: string | React.ReactElement;
  infoItem?: React.ReactElement;
  // availability?: ArticleAvailability;
}

export interface ITwistingSetParams {
  sunSailType: SunSailType;
  poleCount: number;
  heightChangeCount: number;
  deltaZ: number;
  deltaH: number;
}
export interface INetPositionOptions {
  heightChangeOffsets?: number | number[]
}